import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { CURRENCIES } from "currency/model"
import { getAvailableCurrencyIds } from "currency/tools/common"
import { IOrderForm, IOrderFormData, IOrderFormDataErrors, ISaveOrderResult } from "order/model"
import { orderRPO } from "order/repository"
import { orderSVC } from "order/service"
import { orderActionCreator } from "order/store/actions"
import { getDefaultOrderFormState } from "order/store/state"
import { getUserCurrencyId } from "user/tools/common"
import { canAccessDspDeal } from "user/tools/permissions"

export function openCreateOrderPopup(): void {
  getStore().dispatch(orderActionCreator.openFormPopup(getDefaultOrderFormState()))
}

export async function openEditOrderPopup(orderId: number): Promise<void> {
  const form = getDefaultOrderFormState()
  form.data.budget_currency_id = getUserCurrencyId() as CURRENCIES
  form.isCreation = false
  form.orderId = orderId
  form.isLoading = true
  getStore().dispatch(orderActionCreator.openFormPopup(form))
  try {
    const order = await orderRPO.getOrder(orderId)
    const data: IOrderFormData = {
      name: order.name || "",
      notes: order.notes || null,
      budget: order.budget || null,
      budget_currency_id: (order.budget_currency_id || getUserCurrencyId()) as CURRENCIES,
      start_date: order.start_date ? new Date(order.start_date) : null,
      end_date: order.end_date ? new Date(order.end_date) : null,
      max_impressions: order.max_impressions || null,
      unlimitedImpressions: typeof order.max_impressions !== "number" || order.max_impressions === 0,
      advertiserNameOrId: order.advertiser_id
    }
    getStore().dispatch(orderActionCreator.setForm({ isLoading: false, data, initialData: { ...data } }))
  } catch (error) {
    console.error(error)
    getStore().dispatch(orderActionCreator.setForm({ isLoading: false, hasLoadingError: true }))
  }
}

export function closeOrderFormPopup(): void {
  getStore().dispatch(orderActionCreator.closeFormPopup())
}

export function getOrderForm(): IOrderForm {
  return getStore().getState().order.form
}

export function setOrderForm(form: Partial<IOrderForm>): void {
  getStore().dispatch(orderActionCreator.setForm(form))
}

export function validateOrderFormData(
  data: IOrderFormData,
  isDspDeal?: boolean,
  persistDataInStore?: boolean
): IOrderFormDataErrors {
  const errors: IOrderFormDataErrors = {}
  if (!data.name) {
    errors.name = translate("label.field.required")
  }
  if (typeof data.budget === "number") {
    if (!Number.isInteger(data.budget)) {
      errors.budget = translate("label.field.integer")
    } else if (data.budget < 1) {
      errors.budget = translate("label.field.greater.than.or.equals", 1)
    }
  }
  if (typeof data.max_impressions === "number") {
    if (!Number.isInteger(data.max_impressions)) {
      errors.max_impressions = translate("label.field.integer")
    } else if (data.max_impressions < 1) {
      errors.max_impressions = translate("label.field.greater.than.or.equals", 1)
    }
  }
  if (!data.budget_currency_id) {
    errors.budget_currency_id = translate("label.field.required")
  } else if (!getAvailableCurrencyIds().includes(data.budget_currency_id)) {
    errors.budget_currency_id = translate("label.field.invalid.value")
  }
  if (data.start_date && data.end_date && data.start_date > data.end_date) {
    errors.end_date = translate("label.field.date.start.greater.than.end")
  }
  if (!isDspDeal) {
    if (typeof data.advertiserNameOrId === "number" && data.advertiserNameOrId < 0) {
      errors.advertiserNameOrId = translate("label.field.invalid.value")
    }
    if (!data.advertiserNameOrId) {
      if (typeof data.advertiserNameOrId === "string" || data.advertiserNameOrId === null) {
        errors.advertiserNameOrId = translate("label.field.required")
      }
    }
  }
  if (persistDataInStore) {
    getStore().dispatch(
      orderActionCreator.setForm({
        data,
        isValid: JSON.stringify(errors) === "{}",
        errors
      })
    )
  }
  return errors
}

export async function onOrderFormSave(): Promise<ISaveOrderResult> {
  const isDspDeal = canAccessDspDeal()
  const { isSaving, data, hasLoadingError, isValid, isCreation, isLoading, orderId } = getOrderForm()
  if (isSaving || isLoading || !isValid || hasLoadingError) {
    return Promise.reject("Order form is loading, or saving, or not valid, or not loaded well: not able to save it")
  }
  if (isCreation) {
    const result = await orderSVC.createOrder(data, isDspDeal)
    getStore().dispatch(orderActionCreator.add(result.order))
    return result
  } else {
    const result = await orderSVC.updateOrder(orderId, data, isDspDeal)
    getStore().dispatch(orderActionCreator.update(result.order))
    return result
  }
}
